import { z } from "zod";
import {
  PAYMENT_MODES,
  PAYMENT_PROVIDERS,
  PAYMENT_RAILS,
  Trx,
  type IPaymentRoutingSettings,
  type PaymentMode,
  type PaymentProviderName,
  type PaymentRail,
  type TrxCredentials,
} from "@order-up/models";

/**
 * Provider identity. Source of truth is `PAYMENT_PROVIDERS` in
 * `packages/models/src/payments/neutral/enums/PaymentProvider.ts`. Kept
 * in lockstep with the model-side `as const` tuple via the
 * `z.enum([...PAYMENT_PROVIDERS])` spread — adding a new provider in
 * one place automatically propagates here.
 */
/**
 * Payment MODE — whether this restaurant's payments move real money.
 *
 * Validation lives on the WRITE side deliberately. `resolvePaymentMode` on the
 * read side tolerates an unrecognised stored value and falls back to `live`,
 * because a typo must never take a live restaurant's payments offline — which
 * means a misspelt "sandbox" would silently charge real cards. This schema is
 * what stops that value being written in the first place.
 */
export const PaymentModeSchema = z.enum(
  [...PAYMENT_MODES] as [PaymentMode, ...PaymentMode[]],
);

export const PaymentProviderNameSchema = z.enum(
  [...PAYMENT_PROVIDERS] as [PaymentProviderName, ...PaymentProviderName[]],
);

/**
 * Rail enum. Same source-of-truth pattern as
 * `PaymentProviderNameSchema`.
 */
export const PaymentRailSchema = z.enum(
  [...PAYMENT_RAILS] as [PaymentRail, ...PaymentRail[]],
);

/**
 * Provider-routing config stored on the restaurant document as
 * `restaurant.providerRouting`. Every field is optional — an empty object is a
 * structurally valid config, but it resolves to NO provider: the
 * orchestrator fails closed with PAYMENT_PROVIDER_NOT_CONFIGURED rather
 * than falling back to any processor.
 *
 * Mirrors `IPaymentRoutingSettings` from
 * `@order-up/models/payments/neutral/routing/PaymentRouting`. The
 * priority order consumed by `selectPaymentProvider` is documented on
 * the model interface; do not reproduce it here so the two stay in sync.
 *
 * `.strict()` rejects unknown keys so a typo (`defultProvider`) fails
 * loudly at validation rather than silently being persisted as dead
 * data.
 */
const fraudEnforcementSchema = z.enum(["off", "flag", "reject"]);
const neutralAvsOutcomeSchema = z.enum([
  "FullMatch",
  "PostalOnlyMatch",
  "AddressOnlyMatch",
  "NoMatch",
  "NotSupplied",
  "NotProcessed",
]);

/** Per-restaurant fraud-prevention rules — mirrors `IFraudPreventionSettings`. */
export const FraudPreventionSettingsSchema = z
  .object({
    avsCvv: z
      .object({
        enforcement: fraudEnforcementSchema,
        // `.readonly()` to mirror the model's `readonly rejectAvsOutcomes?:
        // readonly NeutralAvsOutcome[]` (FraudPrevention.ts) — matches the
        // `.readonly()` already applied to the enclosing fraudPrevention /
        // providerRouting objects, so a model-typed IPaymentRoutingSettings
        // assigns cleanly into CreateRestaurantType (was a TS2322 readonly→
        // mutable array error at RestaurantModal.tsx). No consumer mutates it.
        rejectAvsOutcomes: z.array(neutralAvsOutcomeSchema).readonly().optional(),
        rejectOnCvvMismatch: z.boolean().optional(),
        cardNotPresentOnly: z.boolean().optional(),
      })
      .strict()
      .optional(),
    negativeDb: z.object({ enforcement: fraudEnforcementSchema }).strict().optional(),
    geoIp: z
      .object({
        enforcement: fraudEnforcementSchema,
        rejectOnCountryMismatch: z.boolean().optional(),
      })
      .strict()
      .optional(),
    addressLookup: z.object({ enforcement: fraudEnforcementSchema }).strict().optional(),
    threeDSecure: z
      .object({ enabled: z.boolean(), requireForCnp: z.boolean().optional() })
      .strict()
      .optional(),
  })
  .strict();

export const PaymentRoutingSettingsSchema = z
  .object({
    defaultProvider: PaymentProviderNameSchema.optional().readonly(),
    /**
     * Zod 4 quirk: `z.record(enumSchema, valueSchema)` is *exhaustive*
     * — it requires a value for every enum member. We want a *partial*
     * map (any subset of rails). `z.partialRecord` is the Zod-4 idiom
     * for "every key from the enum, all optional".
     */
    providerByRail: z
      .partialRecord(PaymentRailSchema, PaymentProviderNameSchema)
      .optional()
      .readonly(),
    /**
     * Open-string key per the model: PaymentSource is intentionally not
     * a closed enum so adding a new source (e.g. a new client app)
     * doesn't require touching this type. Values are still constrained
     * to known providers.
     */
    providerBySource: z
      .record(z.string().min(1), PaymentProviderNameSchema)
      .optional()
      .readonly(),
    forceProvider: PaymentProviderNameSchema.optional().readonly(),
    /**
     * POS-only: use the native TRX SDK card path. Mirrors `IPaymentRoutingSettings.useNativeTrxPath`
     * (was previously missing here — a restaurant carrying it would fail this
     * `.strict()` schema).
     */
    useNativeTrxPath: z.boolean().optional().readonly(),
    /**
     * Enables Level 2 / Level 3 enhanced-data submission on card
     * auth/sale. Off by default — turn on only once the MID is boarded
     * for enhanced data on the processor. See the model interface for
     * the full rationale.
     */
    enhancedDataEnabled: z.boolean().optional().readonly(),
    /**
     * UNSPSC commodity code for L3 qualification, resolved from the
     * restaurant's business type at provisioning (multi-industry — not a
     * constant). Digits only. See the model interface.
     */
    enhancedDataCommodityCode: z
      .string()
      .regex(/^[0-9]+$/, "commodity code must be numeric (UNSPSC)")
      .optional()
      .readonly(),
    /**
     * The MID's boarded processing profile (§22.234 / §22.246 `Industry`),
     * copied from the merchant application at provisioning. Sets the
     * FACE-TO-FACE classifier the TRX adapters send; card-not-present entry
     * modes resolve to `"CardNotPresent"` regardless. See the model interface.
     *
     * `TRX_INDUSTRY_TYPES` (§7.2, six values) rather than the seven-value
     * §22.246 list — the extra member is `"Undefined"`, which §22.246 says the
     * request builder never emits, so it must not be storable here.
     */
    boardedIndustry: z.enum(Trx.TRX_INDUSTRY_TYPES).optional().readonly(),
    fraudPrevention: FraudPreventionSettingsSchema.optional().readonly(),
  })
  .strict();

export type PaymentProviderNameType = z.infer<typeof PaymentProviderNameSchema>;
export type PaymentRailType = z.infer<typeof PaymentRailSchema>;
export type PaymentRoutingSettingsType = z.infer<
  typeof PaymentRoutingSettingsSchema
>;

// ─────────────────────────────────────────────────────────────────────
// Cross-field domain rules
// ─────────────────────────────────────────────────────────────────────
//
// These functions enforce the provider/credential compatibility rules
// at any boundary that mutates a restaurant's payment configuration —
// the Fastify route, the server-side `RestaurantService`, and the
// client-side `restaurantService` (admin app's direct-Firestore writes)
// all call them.
//
// Centralized here, in `@order-up/schemas`, because:
//   * It's pure logic on validated data — no IO, no DI.
//   * Both server and client packages already depend on `@order-up/schemas`.
//   * Putting it in `@order-up/models` would violate the "models = pure
//     interfaces, no logic" rule from CLAUDE.md.
//
// Drift between two implementations of these rules would be a money-
// moving footgun: an admin-app create path that accepts misconfig the
// server rejects (or vice versa) is exactly the kind of split-brain
// validation that produces production incidents.

/**
 * Lightweight error class thrown by the cross-field validators. Carries
 * structured `cause` metadata (provider, conflicting field) so consumers
 * — Fastify's error handler, the admin app's toast surface — can
 * surface a precise client-facing message without parsing the string.
 *
 * Plain `Error` subclass so it's safe to throw across package boundaries
 * without pulling in `@order-up/common`'s `AppError`. Callers that need
 * an HTTP-shaped error wrap it.
 */
export class PaymentRoutingValidationError extends Error {
  /**
   * The provider the rule concerned. A `string`, not `PaymentProviderName`:
   * the unsupported-provider refusal names the stored value it refused
   * (e.g. a retired `"cardpointe"`).
   */
  readonly provider: string;
  readonly field: string;

  constructor(message: string, provider: string, field: string) {
    super(message);
    this.name = "PaymentRoutingValidationError";
    this.provider = provider;
    this.field = field;
  }
}

/**
 * Resolve the *effective* provider implied by routing settings using
 * the same settings-side priority order as
 * `apps/api/components/services/paymentProviderRouter.ts`. Returns
 * `undefined` when settings are ambiguous (e.g. only `providerByRail`
 * set without context to choose a rail) — runtime resolution then
 * applies and we don't enforce a static rule.
 *
 * Used by `validateProviderCredentialConsistency` so cross-field
 * validation runs against the strongest *static* pin (`forceProvider`)
 * when present, falling back to `defaultProvider`.
 */
export function effectiveProvisioningProvider(
  routing: IPaymentRoutingSettings,
): PaymentProviderName | undefined {
  if (routing.forceProvider) return routing.forceProvider;
  if (routing.defaultProvider) return routing.defaultProvider;
  return undefined;
}

/**
 * Bag passed to `validateProviderCredentialConsistency`. One slot per
 * provider's per-restaurant credential shape. Keeping this as a bag
 * (rather than positional args) lets a future provider slot in without
 * reordering call sites.
 */
export interface ProviderCredentialsBag {
  readonly trxCredentials: TrxCredentials | undefined;
}

/**
 * Cross-field validator enforcing provider/credential consistency at
 * the provisioning boundary.
 *
 *   - `effective provider === "trx"` REQUIRES `trxCredentials` with
 *     `clientId`, `sourceId`, and `password` non-empty — the three
 *     fields TRX issues at merchant onboarding. `payPageGuid`, `key`,
 *     and `iv` are optional at provisioning: `payPageGuid` is required
 *     only for hosted-tokenization web checkout (set up later in the
 *     TRX portal) and `key` / `iv` are not consumed by any current
 *     runtime path. Per-endpoint validators (e.g. PayPage init) still
 *     enforce presence of fields they actually need.
 *
 * A new provider adds its own branch with its own required slot; the
 * validator only enforces that the *active* provider's slot is complete.
 *
 * No-op when `routing` is undefined — there is nothing to check for
 * consistency yet. That is NOT an implicit opt-in to any processor: such a
 * location fails closed at `selectPaymentProvider` on its first charge.
 * No-op when the effective provider is undefined (only ambiguous-axis
 * settings — runtime resolution applies).
 *
 * Throws `PaymentRoutingValidationError` on rule violation. Returns
 * silently on success.
 */
export function validateProviderCredentialConsistency(
  routing: IPaymentRoutingSettings | undefined,
  credentials: ProviderCredentialsBag,
): void {
  if (!routing) return;
  const provider = effectiveProvisioningProvider(routing);
  if (!provider) return;

  // A stored value that is no longer a supported provider (a retired
  // processor's "cardpointe") FAILS CLOSED: it is never re-read as TRX, and an
  // edit cannot be saved over it until the routing names a supported provider.
  if (!(PAYMENT_PROVIDERS as readonly string[]).includes(provider)) {
    throw new PaymentRoutingValidationError(
      `Unsupported payment provider "${String(provider)}". Set the location's ` +
        `routing to a supported provider (${PAYMENT_PROVIDERS.join(", ")}).`,
      String(provider),
      "providerRouting",
    );
  }

  const { trxCredentials } = credentials;

  if (provider === "trx") {
    if (!trxCredentials) {
      throw new PaymentRoutingValidationError(
        "TRX restaurant requires trxCredentials. Provider=trx was set " +
          "without a trxCredentials block — gateway calls would fail with " +
          "401 at first transaction. Set restaurant.trxCredentials to " +
          "the values issued by TRX during merchant onboarding " +
          "(clientId, sourceId, password).",
        "trx",
        "trxCredentials",
      );
    }
    const missing: string[] = [];
    if (!trxCredentials.clientId || trxCredentials.clientId.length === 0) {
      missing.push("clientId");
    }
    if (!trxCredentials.sourceId || trxCredentials.sourceId.length === 0) {
      missing.push("sourceId");
    }
    if (!trxCredentials.password || trxCredentials.password.length === 0) {
      missing.push("password");
    }
    if (missing.length > 0) {
      throw new PaymentRoutingValidationError(
        `TRX restaurant trxCredentials is missing required fields: ${missing.join(
          ", ",
        )}. clientId, sourceId, and password are issued by TRX at ` +
          "merchant onboarding and authenticate every gateway call. " +
          "payPageGuid is optional at provisioning — fill it in once a " +
          "PayPage is configured in the TRX portal (required for " +
          "hosted-tokenization web checkout).",
        "trx",
        `trxCredentials.${missing.join(",")}`,
      );
    }
    return;
  }
}
